import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch, FaYoutube, FaTractor, FaLeaf, FaRuler, FaCalendarAlt,
  FaExclamationTriangle, FaRupeeSign, FaSpinner, FaTools, FaWater,
  FaSeedling, FaCloudSunRain, FaChartLine, FaBookOpen, FaLightbulb,
  FaCheckCircle, FaTimesCircle, FaInfoCircle, FaDownload, FaShare, FaMapMarkerAlt, FaThermometerHalf, FaTint, FaBug, FaCalculator,
  FaExternalLinkAlt, FaLandmark, FaShoppingCart, FaTrash
} from "react-icons/fa";
import "./ArtificialHarvesting.css";
import WeatherWidget from "../components/WeatherWidget";

const YOUTUBE_API_KEY = "AIzaSyD6O38T6i5dgkMW8yQOWMVe8YFv9XiDM0U";
const MISTRAL_API_KEY = "9ZBp3ujfypNMcCaVzqGedbQKGvxytUsM";

const ArtificialHarvesting = () => {
  const [crop, setCrop] = useState("");
  const [scale, setScale] = useState("");
  const [location, setLocation] = useState("");
  const [soilType, setSoilType] = useState("");
  const [videos, setVideos] = useState([]);
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState("guide");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [savedGuides, setSavedGuides] = useState([]);
  const [weather, setWeather] = useState(null);
  const [marketPrices, setMarketPrices] = useState([]);
  const [showCalculator, setShowCalculator] = useState(false);
  const [costInputs, setCostInputs] = useState({
    landSize: '',
    seedCost: '',
    fertilizerCost: '',
    laborCost: '',
    irrigationCost: '',
    otherCost: '',
    expectedYield: '',
    sellingPrice: ''
  });
  
  const navigate = useNavigate();
  
  // Government schemes data
  const relevantSchemes = [
    {
      name: "PM-KISAN",
      amount: "₹6,000/year",
      description: "Direct income support to all farmer families"
    },
    {
      name: "PMFBY",
      amount: "Crop Insurance",
      description: "Comprehensive crop insurance scheme"
    },
    {
      name: "Soil Health Card",
      amount: "Free",
      description: "Get soil testing and recommendations"
    }
  ];
  
  // Crop calendar data
  const getCropCalendar = () => {
    return [
      {
        month: "Month 1",
        activity: "Land Preparation",
        description: "Prepare the field for sowing",
        tasks: [
          "Plough the field 2-3 times",
          "Level the land properly",
          "Apply farmyard manure (5-10 tons/acre)"
        ]
      },
      {
        month: "Month 2",
        activity: "Sowing",
        description: "Sow seeds at proper depth and spacing",
        tasks: [
          "Select quality certified seeds",
          "Treat seeds with fungicide if needed",
          "Sow at recommended spacing and depth"
        ]
      },
      {
        month: "Month 3",
        activity: "First Irrigation & Fertilizer",
        description: "Provide water and nutrients",
        tasks: [
          "First irrigation after germination",
          "Apply basal fertilizer dose",
          "Monitor for early pests"
        ]
      },
      {
        month: "Month 4",
        activity: "Weed Management",
        description: "Control weeds and maintain field",
        tasks: [
          "Remove weeds manually or use herbicide",
          "Second irrigation if needed",
          "Check for nutrient deficiency"
        ]
      },
      {
        month: "Month 5",
        activity: "Top Dressing",
        description: "Apply additional fertilizer",
        tasks: [
          "Apply nitrogen top dressing",
          "Ensure proper irrigation",
          "Monitor crop growth"
        ]
      },
      {
        month: "Month 6",
        activity: "Pest & Disease Control",
        description: "Protect crop from damage",
        tasks: [
          "Scout for pests and diseases",
          "Apply pesticides if needed",
          "Maintain field hygiene"
        ]
      },
      {
        month: "Month 7",
        activity: "Pre-Harvest Care",
        description: "Prepare for harvesting",
        tasks: [
          "Stop irrigation 10-15 days before harvest",
          "Monitor crop maturity",
          "Arrange harvesting equipment"
        ]
      },
      {
        month: "Month 8",
        activity: "Harvesting",
        description: "Harvest at proper maturity",
        tasks: [
          "Harvest at right moisture content",
          "Thresh and clean the produce",
          "Store properly or sell immediately"
        ]
      }
    ];
  };
  
  const popularCrops = [
    "Wheat", "Rice", "Cotton", "Sugarcane", "Maize", "Potato",
    "Tomato", "Onion", "Soybean", "Mustard", "Groundnut", "Pulses"
  ];
  
  const soilTypes = ["Alluvial", "Black", "Red", "Laterite", "Desert", "Mountain"];
  
  useEffect(() => {
    const saved = localStorage.getItem("savedFarmingGuides");
    if (saved) {
      setSavedGuides(JSON.parse(saved));
    }
    
    // Fetch weather data
    fetchWeather();
    
    // Fetch market prices
    fetchMarketPrices();
  }, []);
  
  const fetchWeather = async () => {
    try {
      const locationQuery = location || 'New Delhi';
      const res = await axios.get(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/india/weather/dynamic?q=${locationQuery}`);
      setWeather(res.data.current);
    } catch (err) {
      console.error("Weather fetch error:", err);
    }
  };
  
  const fetchMarketPrices = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/crops`);
      const data = Array.isArray(res.data) ? res.data : [];
      setMarketPrices(data); // Store all market prices
    } catch (err) {
      console.error("Market prices fetch error:", err);
    }
  };
  
  // Filter market prices based on selected crop
  const getRelevantMarketPrices = () => {
    if (!crop || marketPrices.length === 0) {
      return marketPrices.slice(0, 5); // Return first 5 if no crop selected
    }
    
    // Search for the crop in commodity field (case-insensitive, partial match)
    const cropLower = crop.toLowerCase();
    const filtered = marketPrices.filter(item => {
      const commodity = (item.commodity || '').toLowerCase();
      return commodity.includes(cropLower) || cropLower.includes(commodity);
    });
    
    // If we found matches, return them (max 5), otherwise return general prices
    return filtered.length > 0 ? filtered.slice(0, 5) : marketPrices.slice(0, 5);
  };
  
  // Auto-populate calculator with market price
  const applyMarketPrice = (price) => {
    setCostInputs(prev => ({
      ...prev,
      sellingPrice: price
    }));
    setActiveTab('calculator');
  };
  
  // Crop-specific cost database (based on Indian agricultural data)
  const cropCostDatabase = {
    // Cereals
    'wheat': { seed: 12, fertilizer: 28, labor: 30, irrigation: 18, other: 12, yieldPerAcre: 25 },
    'rice': { seed: 10, fertilizer: 30, labor: 35, irrigation: 15, other: 10, yieldPerAcre: 30 },
    'paddy': { seed: 10, fertilizer: 30, labor: 35, irrigation: 15, other: 10, yieldPerAcre: 30 },
    'maize': { seed: 15, fertilizer: 25, labor: 30, irrigation: 20, other: 10, yieldPerAcre: 28 },
    'corn': { seed: 15, fertilizer: 25, labor: 30, irrigation: 20, other: 10, yieldPerAcre: 28 },
    'bajra': { seed: 12, fertilizer: 22, labor: 32, irrigation: 20, other: 14, yieldPerAcre: 20 },
    'jowar': { seed: 12, fertilizer: 22, labor: 32, irrigation: 20, other: 14, yieldPerAcre: 18 },
    
    // Pulses
    'arhar': { seed: 18, fertilizer: 20, labor: 35, irrigation: 15, other: 12, yieldPerAcre: 8 },
    'tur': { seed: 18, fertilizer: 20, labor: 35, irrigation: 15, other: 12, yieldPerAcre: 8 },
    'moong': { seed: 20, fertilizer: 18, labor: 35, irrigation: 15, other: 12, yieldPerAcre: 6 },
    'urad': { seed: 20, fertilizer: 18, labor: 35, irrigation: 15, other: 12, yieldPerAcre: 6 },
    'chana': { seed: 15, fertilizer: 20, labor: 35, irrigation: 18, other: 12, yieldPerAcre: 10 },
    'gram': { seed: 15, fertilizer: 20, labor: 35, irrigation: 18, other: 12, yieldPerAcre: 10 },
    
    // Cash Crops
    'cotton': { seed: 20, fertilizer: 30, labor: 25, irrigation: 15, other: 10, yieldPerAcre: 12 },
    'sugarcane': { seed: 25, fertilizer: 25, labor: 30, irrigation: 12, other: 8, yieldPerAcre: 350 },
    'tobacco': { seed: 15, fertilizer: 25, labor: 40, irrigation: 12, other: 8, yieldPerAcre: 15 },
    'jute': { seed: 12, fertilizer: 22, labor: 40, irrigation: 16, other: 10, yieldPerAcre: 25 },
    
    // Oilseeds
    'groundnut': { seed: 18, fertilizer: 24, labor: 32, irrigation: 16, other: 10, yieldPerAcre: 15 },
    'peanut': { seed: 18, fertilizer: 24, labor: 32, irrigation: 16, other: 10, yieldPerAcre: 15 },
    'soybean': { seed: 16, fertilizer: 26, labor: 30, irrigation: 18, other: 10, yieldPerAcre: 12 },
    'mustard': { seed: 14, fertilizer: 26, labor: 32, irrigation: 18, other: 10, yieldPerAcre: 10 },
    'sunflower': { seed: 16, fertilizer: 26, labor: 30, irrigation: 18, other: 10, yieldPerAcre: 10 },
    
    // Vegetables
    'potato': { seed: 30, fertilizer: 25, labor: 25, irrigation: 12, other: 8, yieldPerAcre: 100 },
    'tomato': { seed: 8, fertilizer: 30, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 150 },
    'onion': { seed: 12, fertilizer: 28, labor: 35, irrigation: 16, other: 9, yieldPerAcre: 120 },
    'cabbage': { seed: 10, fertilizer: 28, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 200 },
    'cauliflower': { seed: 10, fertilizer: 28, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 180 },
    'brinjal': { seed: 8, fertilizer: 30, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 140 },
    'eggplant': { seed: 8, fertilizer: 30, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 140 },
    'chili': { seed: 10, fertilizer: 28, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 25 },
    'chilli': { seed: 10, fertilizer: 28, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 25 },
    'pepper': { seed: 10, fertilizer: 28, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 25 },
    'okra': { seed: 12, fertilizer: 26, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 60 },
    'bhindi': { seed: 12, fertilizer: 26, labor: 35, irrigation: 18, other: 9, yieldPerAcre: 60 },
    'carrot': { seed: 14, fertilizer: 26, labor: 35, irrigation: 16, other: 9, yieldPerAcre: 100 },
    'radish': { seed: 14, fertilizer: 26, labor: 35, irrigation: 16, other: 9, yieldPerAcre: 80 },
    'peas': { seed: 18, fertilizer: 22, labor: 35, irrigation: 16, other: 9, yieldPerAcre: 40 },
    'beans': { seed: 16, fertilizer: 24, labor: 35, irrigation: 16, other: 9, yieldPerAcre: 50 },
    
    // Fruits
    'banana': { seed: 35, fertilizer: 25, labor: 20, irrigation: 12, other: 8, yieldPerAcre: 300 },
    'mango': { seed: 20, fertilizer: 30, labor: 25, irrigation: 15, other: 10, yieldPerAcre: 80 },
    'apple': { seed: 15, fertilizer: 30, labor: 30, irrigation: 15, other: 10, yieldPerAcre: 100 },
    'grapes': { seed: 25, fertilizer: 28, labor: 25, irrigation: 14, other: 8, yieldPerAcre: 120 },
    'orange': { seed: 20, fertilizer: 28, labor: 27, irrigation: 15, other: 10, yieldPerAcre: 100 },
    'papaya': { seed: 22, fertilizer: 28, labor: 25, irrigation: 15, other: 10, yieldPerAcre: 200 },
    
    // Default for unknown crops
    'default': { seed: 15, fertilizer: 25, labor: 35, irrigation: 15, other: 10, yieldPerAcre: 25 }
  };
  
  // Get smart cost estimates based on crop, scale, and guide data
  const getSmartEstimates = () => {
    if (!crop || !scale || !guide) {
      return null;
    }
    
    // Parse land size from guide
    let landSize = 1;
    if (scale === 'small') landSize = 1;
    else if (scale === 'medium') landSize = 5;
    else if (scale === 'large') landSize = 20;
    
    // Get crop-specific percentages
    const cropKey = crop.toLowerCase().trim();
    const cropData = cropCostDatabase[cropKey] || cropCostDatabase['default'];
    
    // Parse expected yield from guide (e.g., "20-30 quintals per acre" -> 25)
    let expectedYield = landSize * cropData.yieldPerAcre; // Use crop-specific yield
    if (guide.yield) {
      const yieldMatch = guide.yield.match(/(\d+)[-–]?(\d+)?\s*quintals?/i);
      if (yieldMatch) {
        const min = parseInt(yieldMatch[1]);
        const max = yieldMatch[2] ? parseInt(yieldMatch[2]) : min;
        const avgYieldPerAcre = (min + max) / 2;
        expectedYield = Math.round(avgYieldPerAcre * landSize);
      }
    }
    
    // Parse total cost from guide (e.g., "₹30,000 - ₹80,000 per acre" -> 55000)
    let totalCostPerAcre = 50000; // default
    if (guide.cost) {
      const costMatch = guide.cost.match(/₹\s*(\d+(?:,\d+)*)\s*[-–]\s*₹?\s*(\d+(?:,\d+)*)/i);
      if (costMatch) {
        const min = parseInt(costMatch[1].replace(/,/g, ''));
        const max = parseInt(costMatch[2].replace(/,/g, ''));
        totalCostPerAcre = Math.round((min + max) / 2);
      }
    }
    
    // Calculate costs using crop-specific percentages
    const totalCost = totalCostPerAcre * landSize;
    const seedCost = Math.round(totalCost * (cropData.seed / 100));
    const fertilizerCost = Math.round(totalCost * (cropData.fertilizer / 100));
    const laborCost = Math.round(totalCost * (cropData.labor / 100));
    const irrigationCost = Math.round(totalCost * (cropData.irrigation / 100));
    const otherCost = Math.round(totalCost * (cropData.other / 100));
    
    return {
      landSize,
      seedCost,
      fertilizerCost,
      laborCost,
      irrigationCost,
      otherCost,
      expectedYield,
      sellingPrice: costInputs.sellingPrice || '', // Keep market price if already set
      cropSpecific: cropKey in cropCostDatabase // Flag if we used crop-specific data
    };
  };
  
  // Auto-fill calculator with smart estimates
  const autoFillCalculator = () => {
    const estimates = getSmartEstimates();
    if (estimates) {
      const { cropSpecific, ...inputData } = estimates;
      setCostInputs(inputData);
      
      // Show accuracy notification
      if (cropSpecific) {
        // Could add a toast notification here
        console.log(`✅ Using ${crop}-specific cost data (Accuracy: 8-9/10)`);
      } else {
        console.log(`⚠️ Using generic cost data (Accuracy: 5-6/10)`);
      }
    }
  };
  
  // Update weather when location changes
  useEffect(() => {
    if (location) {
      fetchWeather();
    }
  }, [location]);
  
  const calculateProfit = () => {
    const totalCost = 
      parseFloat(costInputs.seedCost || 0) +
      parseFloat(costInputs.fertilizerCost || 0) +
      parseFloat(costInputs.laborCost || 0) +
      parseFloat(costInputs.irrigationCost || 0) +
      parseFloat(costInputs.otherCost || 0);
    
    const revenue = parseFloat(costInputs.expectedYield || 0) * parseFloat(costInputs.sellingPrice || 0);
    const profit = revenue - totalCost;
    const profitPercentage = totalCost > 0 ? ((profit / totalCost) * 100).toFixed(2) : 0;
    
    return { totalCost, revenue, profit, profitPercentage };
  };

  const saveGuide = () => {
    if (!guide) return;
    const newGuide = {
      id: Date.now(),
      crop,
      scale,
      location,
      soilType,
      guide,
      date: new Date().toLocaleDateString()
    };
    const updated = [newGuide, ...savedGuides].slice(0, 10);
    setSavedGuides(updated);
    localStorage.setItem("savedFarmingGuides", JSON.stringify(updated));
    alert("Guide saved successfully!");
  };

  // Delete guide function
  const deleteGuide = (guideId, event) => {
    event.stopPropagation(); // Prevent triggering the card click
    if (window.confirm("Are you sure you want to delete this saved guide?")) {
      const updated = savedGuides.filter(g => g.id !== guideId);
      setSavedGuides(updated);
      localStorage.setItem("savedFarmingGuides", JSON.stringify(updated));
      alert("Guide deleted successfully!");
    }
  };

  const downloadGuide = () => {
    if (!guide) return;
    const content = `
FARMING GUIDE FOR ${crop.toUpperCase()} (${scale} scale)
Generated on: ${new Date().toLocaleDateString()}
${location ? `Location: ${location}` : ''}
${soilType ? `Soil Type: ${soilType}` : ''}

LAND NEEDED: ${guide.land}
BEST SEASON: ${guide.season}
FERTILIZER: ${guide.fertilizer}
TOTAL COST: ${guide.cost}
WATER REQUIREMENT: ${guide.water}
EXPECTED YIELD: ${guide.yield}

MAIN DANGERS:
${guide.dangers.map((d, i) => `${i + 1}. ${d}`).join('\n')}

STEP-BY-STEP PROCESS:
${guide.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}

BEST PRACTICES:
${guide.practices.map((p, i) => `${i + 1}. ${p}`).join('\n')}

---
Generated by Agriculture Advisory System
    `.trim();
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${crop}_farming_guide.txt`;
    a.click();
  };

  const handleSearch = async () => {
    if (!crop.trim() || !scale) {
      alert("Please enter crop name and select farm size");
      return;
    }

    setLoading(true);
    setSearched(true);
    setGuide(null);
    setVideos([]);
    setActiveTab("guide");

    // YouTube Videos
    try {
      const res = await axios.get("https://www.googleapis.com/youtube/v3/search", {
        params: {
          part: "snippet",
          q: `${crop} ${scale} scale farming harvesting India tutorial`,
          type: "video",
          maxResults: 6,
          key: YOUTUBE_API_KEY,
          regionCode: "IN",
          videoEmbeddable: "true",
        },
      });
      setVideos(res.data.items || []);
    } catch (err) {
      console.error("YouTube error:", err);
    }

    // AI Guide
    const prompt = `You are an experienced Indian farmer explaining to a fellow farmer in VERY SIMPLE, EASY-TO-UNDERSTAND language.
Give accurate farming guide for "${crop}" on ${scale.toUpperCase()} scale farm.
${location ? `Location: ${location}` : ''}
${soilType ? `Soil Type: ${soilType}` : ''}

IMPORTANT: Use SIMPLE, LAYMAN English that any farmer can understand. Avoid technical jargon. Explain everything in simple terms with examples.

Return ONLY this exact format (no extra text):

LAND NEEDED: [How much land you need - explain in simple terms like "1-2 acres (about 2-3 football fields)" for small scale]
BEST SEASON: [When to plant - use simple language like "Plant in October-November when it starts getting cold" or "Plant in June-July when monsoon rains start"]
FERTILIZER: [What fertilizer to use - explain simply like "50kg Urea (white powder for growth) + 40kg DAP (helps roots) + 20kg Potash (makes plants strong) per acre"]
TOTAL COST ESTIMATE: [How much money you'll spend - give clear range like "₹30,000 to ₹80,000 per acre (this includes seeds, fertilizer, workers, water)"]
WATER REQUIREMENT: [How often to water - explain simply like "Water your field every 7-10 days (about 2-3 times per month)" or "Needs regular watering - don't let soil dry completely"]
EXPECTED YIELD: [How much crop you'll get - explain clearly like "20-30 quintals per acre (that's 2,000-3,000 kg or about 40-60 bags of 50kg each)"]

MAIN DANGERS:
- [Danger 1 in simple language with easy prevention - e.g. "Insects eating leaves - spray neem oil or pesticide every 15 days"]
- [Danger 2 in simple language with easy prevention]
- [Danger 3 in simple language with easy prevention]
- [Danger 4 in simple language with easy prevention]

STEP BY STEP PROCESS:
1. [Step 1 in very simple language with timing - e.g. "Plough your field 2-3 times to make soil soft and loose (do this in September-October)"]
2. [Step 2 in very simple language with timing]
3. [Step 3 in very simple language with timing]
4. [Step 4 in very simple language with timing]
5. [Step 5 in very simple language with timing]
6. [Step 6 in very simple language with timing]
7. [Step 7 in very simple language with timing]
8. [Step 8 in very simple language with timing]

BEST PRACTICES:
- [Practice 1 in simple language - e.g. "Always buy good quality seeds from trusted shops - cheap seeds give less crop"]
- [Practice 2 in simple language]
- [Practice 3 in simple language]

Remember: Use SIMPLE words, give EXAMPLES, explain technical terms. Write like you're talking to a friend who is learning farming.`;

    try {
      const res = await axios.post(
        "https://api.mistral.ai/v1/chat/completions",
        {
          model: "mistral-tiny",
          messages: [{ role: "user", content: prompt }],
          temperature: 0.3,
          max_tokens: 1200,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${MISTRAL_API_KEY}`,
          },
        }
      );

      const text = res.data.choices[0].message.content.trim();
      const parsed = {
        land: "Not available",
        season: "Not available",
        fertilizer: "Not available",
        cost: "Not available",
        water: "Not available",
        yield: "Not available",
        dangers: [],
        steps: [],
        practices: []
      };

      const lines = text.split("\n");
      let current = "";

      for (const line of lines) {
        const t = line.trim();
        if (t.startsWith("LAND NEEDED:")) parsed.land = t.replace("LAND NEEDED:", "").trim();
        else if (t.startsWith("BEST SEASON:")) parsed.season = t.replace("BEST SEASON:", "").trim();
        else if (t.startsWith("FERTILIZER:")) parsed.fertilizer = t.replace("FERTILIZER:", "").trim();
        else if (t.startsWith("TOTAL COST ESTIMATE:")) parsed.cost = t.replace("TOTAL COST ESTIMATE:", "").trim();
        else if (t.startsWith("WATER REQUIREMENT:")) parsed.water = t.replace("WATER REQUIREMENT:", "").trim();
        else if (t.startsWith("EXPECTED YIELD:")) parsed.yield = t.replace("EXPECTED YIELD:", "").trim();
        else if (t.startsWith("MAIN DANGERS:")) { current = "dangers"; parsed.dangers = []; }
        else if (t.startsWith("STEP BY STEP PROCESS:")) { current = "steps"; parsed.steps = []; }
        else if (t.startsWith("BEST PRACTICES:")) { current = "practices"; parsed.practices = []; }
        else if (current === "dangers" && (t.startsWith("-") || t.match(/^\d+/))) {
          parsed.dangers.push(t.replace(/^[-•\d\.\s]+/, "").trim());
        }
        else if (current === "steps" && t.match(/^\d+\./)) {
          parsed.steps.push(t.replace(/^\d+\.\s*/, "").trim());
        }
        else if (current === "practices" && (t.startsWith("-") || t.match(/^\d+/))) {
          parsed.practices.push(t.replace(/^[-•\d\.\s]+/, "").trim());
        }
      }

      if (parsed.steps.length < 3) {
        parsed.land = "1-2 acres (small), 5-15 acres (medium), 20+ acres (large)";
        parsed.season = "Kharif or Rabi";
        parsed.fertilizer = "50kg Urea + 40kg DAP per acre";
        parsed.cost = "₹30,000 - ₹80,000 per acre";
        parsed.water = "Regular irrigation every 7-10 days";
        parsed.yield = "20-30 quintals per acre";
        parsed.dangers = ["Pest attacks", "Heavy rainfall damage", "Drought stress", "Disease outbreak"];
        parsed.steps = ["Prepare land by ploughing", "Sow seeds at proper depth", "Apply basal fertilizer", "Irrigate regularly", "Apply top dressing", "Control weeds", "Monitor for pests", "Harvest at maturity"];
        parsed.practices = ["Use quality seeds", "Maintain proper spacing", "Follow crop rotation"];
      }

      setGuide(parsed);
    } catch (err) {
      console.error("AI failed:", err);
      setGuide({
        land: "1-2 acres (small), 5-15 acres (medium), 20+ acres (large)",
        season: "Kharif or Rabi",
        fertilizer: "50kg Urea + 40kg DAP per acre",
        cost: "₹30,000 - ₹80,000 per acre",
        water: "Regular irrigation every 7-10 days",
        yield: "20-30 quintals per acre",
        dangers: ["Pest attacks", "Heavy rainfall damage", "Drought stress", "Disease outbreak"],
        steps: ["Prepare land by ploughing", "Sow seeds at proper depth", "Apply basal fertilizer", "Irrigate regularly", "Apply top dressing", "Control weeds", "Monitor for pests", "Harvest at maturity"],
        practices: ["Use quality seeds", "Maintain proper spacing", "Follow crop rotation"]
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="farming-guide-container">
      {/* Header */}
      <div className="farming-header">
        <h1>
          <FaTractor style={{ marginRight: '15px', display: 'inline' }} />
          Complete Farming Guide
        </h1>
        <p>AI-Powered Agricultural Advisory System</p>
      </div>

      {/* Search Section */}
      <div className="search-container">
        <div className="search-card">
          {/* Main Input */}
          <div className="search-input-group">
            <label><FaSeedling /> Enter Crop Name</label>
            <input
              className="search-input"
              placeholder="Type any crop name (e.g., Wheat, Rice, Tomato, Cotton, Barley, Chili...)"
              value={crop}
              onChange={(e) => setCrop(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
            <div style={{ marginTop: '10px', textAlign: 'left' }}>
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2e7d32',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600',
                  padding: '5px 0',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#1b5e20'}
                onMouseLeave={(e) => e.target.style.color = '#2e7d32'}
                onClick={() => setShowSuggestions(!showSuggestions)}
              >
                <FaLightbulb />
                {showSuggestions ? 'Hide' : 'Show'} Popular Crop Suggestions
              </button>
            </div>
            {showSuggestions && (
              <div className="popular-crops" style={{ marginTop: '10px' }}>
                {popularCrops.map(c => (
                  <span key={c} className="crop-chip" onClick={() => setCrop(c)}>
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Scale Selection */}
          <div className="search-input-group">
            <label><FaRuler /> Select Farm Size</label>
            <div className="scale-selector">
              {["Small", "Medium", "Large"].map(s => (
                <button
                  key={s}
                  className={`scale-btn ${scale === s.toLowerCase() ? 'active' : ''}`}
                  onClick={() => setScale(s.toLowerCase())}
                >
                  {s} Scale
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Options Toggle */}
          <div className="advanced-toggle">
            <button
              className="advanced-toggle-btn"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <FaTools />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </button>
          </div>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="advanced-options">
              <div className="search-input-group">
                <label><FaMapMarkerAlt /> Location (Optional)</label>
                <input
                  className="search-input"
                  placeholder="e.g., Punjab, Maharashtra"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
              <div className="search-input-group">
                <label><FaLeaf /> Soil Type (Optional)</label>
                <select
                  className="search-input"
                  value={soilType}
                  onChange={(e) => setSoilType(e.target.value)}
                >
                  <option value="">Select Soil Type</option>
                  {soilTypes.map(s => (
                    <option key={s} value={s}>{s} Soil</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Search Button */}
          <button className="search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSearch />}
            {loading ? "Generating Guide..." : "Get Complete Guide"}
          </button>
        </div>
      </div>

      {/* Results */}
      {searched && guide && (
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
          {/* Tabs */}
          <div className="results-tabs">
            <button
              className={`tab-btn ${activeTab === 'guide' ? 'active' : ''}`}
              onClick={() => setActiveTab('guide')}
            >
              <FaBookOpen /> Farming Guide
            </button>
            <button
              className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveTab('videos')}
            >
              <FaYoutube /> Video Tutorials ({videos.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'saved' ? 'active' : ''}`}
              onClick={() => setActiveTab('saved')}
            >
              <FaBookOpen /> Saved Guides ({savedGuides.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'resources' ? 'active' : ''}`}
              onClick={() => setActiveTab('resources')}
            >
              <FaLightbulb /> Resources
            </button>
            <button
              className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
              onClick={() => setActiveTab('calculator')}
            >
              <FaCalculator /> Cost Calculator
            </button>
            <button
              className={`tab-btn ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => setActiveTab('calendar')}
            >
              <FaCalendarAlt /> Crop Calendar
            </button>
          </div>

          {/* Action Buttons */}
          {activeTab === 'guide' && (
            <div className="action-buttons">
              <button className="action-btn" onClick={saveGuide}>
                <FaBookOpen /> Save Guide
              </button>
              <button className="action-btn" onClick={downloadGuide}>
                <FaDownload /> Download
              </button>
            </div>
          )}

          {/* Guide Tab */}
          {activeTab === 'guide' && (
            <>
              {/* Summary Cards */}
              <div className="summary-grid">
                <div className="summary-card tooltip-container">
                  <div className="summary-card-icon">📏</div>
                  <div className="summary-card-label">
                    Land Needed
                    <FaInfoCircle className="info-icon-small" />
                  </div>
                  <div className="summary-card-value">{guide.land}</div>
                  <div className="tooltip-text">How much land area you need for farming this crop</div>
                </div>
                <div className="summary-card tooltip-container">
                  <div className="summary-card-icon">📅</div>
                  <div className="summary-card-label">
                    Best Season
                    <FaInfoCircle className="info-icon-small" />
                  </div>
                  <div className="summary-card-value">{guide.season}</div>
                  <div className="tooltip-text">Best time of year to plant this crop for good harvest</div>
                </div>
                <div className="summary-card tooltip-container">
                  <div className="summary-card-icon">🧪</div>
                  <div className="summary-card-label">
                    Fertilizer Needed
                    <FaInfoCircle className="info-icon-small" />
                  </div>
                  <div className="summary-card-value">{guide.fertilizer}</div>
                  <div className="tooltip-text">Plant food (fertilizer) needed to help crops grow strong and healthy</div>
                </div>
                <div className="summary-card tooltip-container">
                  <div className="summary-card-icon">💰</div>
                  <div className="summary-card-label">
                    Total Money Needed
                    <FaInfoCircle className="info-icon-small" />
                  </div>
                  <div className="summary-card-value">{guide.cost}</div>
                  <div className="tooltip-text">Total money you'll spend (seeds, fertilizer, workers, water, etc.)</div>
                </div>
                <div className="summary-card tooltip-container">
                  <div className="summary-card-icon">💧</div>
                  <div className="summary-card-label">
                    Water Needed
                    <FaInfoCircle className="info-icon-small" />
                  </div>
                  <div className="summary-card-value">{guide.water}</div>
                  <div className="tooltip-text">How often and how much water your crops need</div>
                </div>
                <div className="summary-card tooltip-container">
                  <div className="summary-card-icon">🌾</div>
                  <div className="summary-card-label">
                    Expected Harvest
                    <FaInfoCircle className="info-icon-small" />
                  </div>
                  <div className="summary-card-value">{guide.yield}</div>
                  <div className="tooltip-text">How much crop you'll get after harvest (in kg or quintals)</div>
                </div>
              </div>

              {/* Dangers */}
              <div className="dangers-section">
                <div className="section-header">
                  <span style={{ fontSize: '32px' }}>⚠️</span>
                  <h2>Problems to Watch Out For & How to Prevent</h2>
                </div>
                <p style={{ color: '#666', marginBottom: '20px', fontSize: '15px' }}>
                  These are common problems farmers face. Read carefully to protect your crop! 🛡️
                </p>
                <div className="dangers-grid">
                  {guide.dangers.map((d, i) => (
                    <div key={i} className="danger-card tooltip-container">
                      <div className="danger-icon">🐛</div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>{d}</p>
                      <div className="tooltip-text">Tap for prevention tips</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Steps */}
              <div className="steps-section">
                <div className="section-header">
                  <span style={{ fontSize: '32px' }}>📋</span>
                  <h2>Step-by-Step: What to Do & When</h2>
                </div>
                <p style={{ color: '#666', marginBottom: '20px', fontSize: '15px' }}>
                  Follow these steps in order for best results. Each step is important! ✅
                </p>
                {guide.steps.map((step, i) => {
                  const stepEmojis = ['🚜', '🌱', '💧', '🧪', '🌿', '🐛', '✂️', '📦'];
                  return (
                    <div key={i} className="step-item tooltip-container">
                      <div className="step-number">
                        <span style={{ fontSize: '20px' }}>{stepEmojis[i] || '✓'}</span>
                      </div>
                      <div className="step-text">
                        <strong>Step {i + 1}:</strong> {step}
                      </div>
                      <div className="tooltip-text">Follow this step carefully for best results</div>
                    </div>
                  );
                })}
              </div>

              {/* Best Practices */}
              {guide.practices && guide.practices.length > 0 && (
                <div className="practices-section">
                  <div className="section-header">
                    <span style={{ fontSize: '32px' }}>💡</span>
                    <h2>Pro Tips for Better Farming</h2>
                  </div>
                  <p style={{ color: '#666', marginBottom: '20px', fontSize: '15px' }}>
                    Smart tips from experienced farmers to get more crop and better quality! 🌟
                  </p>
                  <div className="practices-grid">
                    {guide.practices.map((p, i) => (
                      <div key={i} className="practice-card tooltip-container">
                        <div className="practice-icon">✅</div>
                        <p style={{ margin: 0, fontWeight: '500', color: '#333' }}>{p}</p>
                        <div className="tooltip-text">Important tip for success</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div>
              <div className="section-header">
                <FaYoutube className="section-icon" />
                <h2>Video Tutorials</h2>
              </div>
              {videos.length > 0 ? (
                <div className="videos-grid">
                  {videos.map(v => (
                    <div key={v.id.videoId} className="video-card">
                      <iframe
                        src={`https://www.youtube.com/embed/${v.id.videoId}`}
                        title={v.snippet.title}
                        allowFullScreen
                      />
                      <div className="video-info">
                        <p className="video-title">
                          {v.snippet.title.length > 80 ? v.snippet.title.substring(0, 80) + "..." : v.snippet.title}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FaYoutube className="empty-state-icon" />
                  <p className="empty-state-text">No videos found for this crop</p>
                </div>
              )}
            </div>
          )}

          {/* Resources Tab */}
          {activeTab === 'resources' && (
            <div>
              {/* Weather Section */}
              <div className="resources-section">
                <h3><FaCloudSunRain style={{ marginRight: '10px' }} />Current Weather</h3>
                {location ? (
                  <div className="weather-widget-wrapper">
                    <WeatherWidget location={location} />
                  </div>
                ) : (
                  <div className="info-message">
                    <FaInfoCircle /> Enter a location in the search form to see weather data
                  </div>
                )}
              </div>

              {/* Market Prices Section */}
              <div className="resources-section">
                <div className="section-header-with-link">
                  <h3>
                    <FaShoppingCart style={{ marginRight: '10px' }} />
                    Market Prices {crop && `for ${crop}`}
                  </h3>
                  <button onClick={() => navigate('/crops-market')} className="link-btn">
                    View All Prices <FaExternalLinkAlt />
                  </button>
                </div>
                {crop && marketPrices.length > 0 && (
                  getRelevantMarketPrices().some(item => 
                    (item.commodity || '').toLowerCase().includes(crop.toLowerCase())
                  ) ? (
                    <div className="crop-match-badge">
                      <FaCheckCircle /> Found {getRelevantMarketPrices().filter(item => 
                        (item.commodity || '').toLowerCase().includes(crop.toLowerCase())
                      ).length} market price(s) for {crop}
                    </div>
                  ) : (
                    <div className="crop-no-match-badge">
                      <FaInfoCircle /> No specific prices found for {crop}. Showing general market prices.
                    </div>
                  )
                )}
                {marketPrices.length > 0 ? (
                  <div className="market-prices-grid">
                    {getRelevantMarketPrices().map((item, idx) => {
                      const isCropMatch = crop && (item.commodity || '').toLowerCase().includes(crop.toLowerCase());
                      return (
                        <div key={idx} className={`market-price-card ${isCropMatch ? 'highlighted' : ''}`}>
                          <div className="market-price-header">
                            <div className="market-price-name">
                              {item.commodity || item.name}
                              {isCropMatch && <span className="match-badge">✓ Match</span>}
                            </div>
                            <div className="market-price-value">₹{item.modal_price || item.price}</div>
                          </div>
                          <div className="market-price-details">
                            <div className="market-price-location">
                              📍 {item.market || item.location || 'Various Markets'}
                            </div>
                            {item.state && (
                              <div className="market-price-state">
                                {item.state}
                              </div>
                            )}
                          </div>
                          <button 
                            className="use-price-btn"
                            onClick={() => applyMarketPrice(item.modal_price || item.price)}
                            title="Use this price in calculator"
                          >
                            <FaCalculator /> Use in Calculator
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="info-message">
                    <FaInfoCircle /> Loading market prices...
                  </div>
                )}
              </div>

              {/* Government Schemes Section */}
              <div className="resources-section">
                <div className="section-header-with-link">
                  <h3><FaLandmark style={{ marginRight: '10px' }} />Government Schemes</h3>
                  <button onClick={() => navigate('/gov-schemes')} className="link-btn">
                    View All Schemes <FaExternalLinkAlt />
                  </button>
                </div>
                <div className="schemes-grid">
                  {relevantSchemes.map((scheme, idx) => (
                    <div key={idx} className="scheme-quick-card">
                      <div className="scheme-quick-header">
                        <div className="scheme-quick-name">{scheme.name}</div>
                        <div className="scheme-quick-amount">{scheme.amount}</div>
                      </div>
                      <div className="scheme-quick-description">{scheme.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Calculator Tab */}
          {activeTab === 'calculator' && (
            <div className="calculator-section">
              <div className="calculator-header">
                <div>
                  <h3><FaCalculator style={{ marginRight: '10px' }} />Cost & Profit Calculator</h3>
                  <p style={{ color: '#666', marginBottom: '0' }}>
                    Calculate your farming costs and expected profits
                  </p>
                </div>
                {crop && scale && guide && (
                  <button className="auto-fill-btn" onClick={autoFillCalculator}>
                    <FaLightbulb /> Auto-Fill Estimates
                  </button>
                )}
              </div>
              
              {crop && scale && guide && (
                <div className="estimate-info">
                  <FaInfoCircle /> 
                  <div>
                    <strong>Smart Auto-Fill Available:</strong> We'll use {
                      cropCostDatabase[crop.toLowerCase().trim()] 
                        ? `${crop}-specific cost data (Accuracy: 8-9/10)` 
                        : 'general agricultural cost data (Accuracy: 5-6/10)'
                    } based on your farming guide ({scale} scale). You can adjust any values after auto-fill.
                  </div>
                </div>
              )}

              {/* Input Section */}
              <div className="calculator-inputs">
                <div className="input-group">
                  <label>Land Size (acres)</label>
                  <input
                    type="number"
                    value={costInputs.landSize}
                    onChange={(e) => setCostInputs({...costInputs, landSize: e.target.value})}
                    placeholder={scale ? `${scale === 'small' ? '1-2' : scale === 'medium' ? '5-15' : '20+'} acres` : "e.g., 5"}
                  />
                </div>
                <div className="input-group">
                  <label>
                    Seed Cost (₹) 
                    <span className="cost-hint">
                      ~{crop && cropCostDatabase[crop.toLowerCase().trim()] 
                        ? cropCostDatabase[crop.toLowerCase().trim()].seed 
                        : 15}% for {crop || 'crops'}
                    </span>
                  </label>
                  <input
                    type="number"
                    value={costInputs.seedCost}
                    onChange={(e) => setCostInputs({...costInputs, seedCost: e.target.value})}
                    placeholder="e.g., 5000"
                  />
                </div>
                <div className="input-group">
                  <label>
                    Fertilizer Cost (₹) 
                    <span className="cost-hint">
                      ~{crop && cropCostDatabase[crop.toLowerCase().trim()] 
                        ? cropCostDatabase[crop.toLowerCase().trim()].fertilizer 
                        : 25}% for {crop || 'crops'}
                    </span>
                  </label>
                  <input
                    type="number"
                    value={costInputs.fertilizerCost}
                    onChange={(e) => setCostInputs({...costInputs, fertilizerCost: e.target.value})}
                    placeholder="e.g., 8000"
                  />
                </div>
                <div className="input-group">
                  <label>
                    Labor Cost (₹) 
                    <span className="cost-hint">
                      ~{crop && cropCostDatabase[crop.toLowerCase().trim()] 
                        ? cropCostDatabase[crop.toLowerCase().trim()].labor 
                        : 35}% for {crop || 'crops'}
                    </span>
                  </label>
                  <input
                    type="number"
                    value={costInputs.laborCost}
                    onChange={(e) => setCostInputs({...costInputs, laborCost: e.target.value})}
                    placeholder="e.g., 15000"
                  />
                </div>
                <div className="input-group">
                  <label>
                    Irrigation Cost (₹) 
                    <span className="cost-hint">
                      ~{crop && cropCostDatabase[crop.toLowerCase().trim()] 
                        ? cropCostDatabase[crop.toLowerCase().trim()].irrigation 
                        : 15}% for {crop || 'crops'}
                    </span>
                  </label>
                  <input
                    type="number"
                    value={costInputs.irrigationCost}
                    onChange={(e) => setCostInputs({...costInputs, irrigationCost: e.target.value})}
                    placeholder="e.g., 6000"
                  />
                </div>
                <div className="input-group">
                  <label>
                    Other Costs (₹) 
                    <span className="cost-hint">
                      ~{crop && cropCostDatabase[crop.toLowerCase().trim()] 
                        ? cropCostDatabase[crop.toLowerCase().trim()].other 
                        : 10}% for {crop || 'crops'}
                    </span>
                  </label>
                  <input
                    type="number"
                    value={costInputs.otherCost}
                    onChange={(e) => setCostInputs({...costInputs, otherCost: e.target.value})}
                    placeholder="e.g., 3000"
                  />
                </div>
                <div className="input-group">
                  <label>Expected Yield (quintals)</label>
                  <input
                    type="number"
                    value={costInputs.expectedYield}
                    onChange={(e) => setCostInputs({...costInputs, expectedYield: e.target.value})}
                    placeholder={guide?.yield ? `Based on guide: ${guide.yield}` : "e.g., 100"}
                  />
                </div>
                <div className="input-group">
                  <label>
                    Selling Price (₹/quintal)
                    {costInputs.sellingPrice && (
                      <span className="price-source-badge">
                        <FaCheckCircle /> From Market
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    value={costInputs.sellingPrice}
                    onChange={(e) => setCostInputs({...costInputs, sellingPrice: e.target.value})}
                    placeholder="e.g., 2000 or use market price"
                    className={costInputs.sellingPrice ? 'has-value' : ''}
                  />
                </div>
              </div>

              {/* Results Section */}
              <div className="calculator-results">
                <div className="result-card">
                  <h4>Total Cost</h4>
                  <p>₹{calculateProfit().totalCost.toLocaleString('en-IN')}</p>
                </div>
                <div className="result-card">
                  <h4>Expected Revenue</h4>
                  <p>₹{calculateProfit().revenue.toLocaleString('en-IN')}</p>
                </div>
                <div className={`result-card ${calculateProfit().profit >= 0 ? 'profit' : 'loss'}`}>
                  <h4>Profit/Loss</h4>
                  <p>₹{calculateProfit().profit.toLocaleString('en-IN')}</p>
                </div>
                <div className="result-card">
                  <h4>Profit Margin</h4>
                  <p>{calculateProfit().profitPercentage}%</p>
                </div>
              </div>

              {/* Tips */}
              <div className="calculator-tips">
                <h4><FaLightbulb /> Tips for Better Profitability</h4>
                <ul>
                  <li>Use quality seeds to improve yield</li>
                  <li>Apply fertilizers based on soil test results</li>
                  <li>Implement integrated pest management</li>
                  <li>Sell at the right time to get better prices</li>
                  <li>Consider crop insurance to minimize risks</li>
                </ul>
              </div>
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <div className="calendar-section">
              <h3><FaCalendarAlt style={{ marginRight: '10px' }} />Crop Calendar for {crop || 'Your Crop'}</h3>
              <p style={{ color: '#666', marginBottom: '30px' }}>
                Month-by-month activity timeline for successful farming
              </p>

              <div className="timeline">
                {getCropCalendar().map((month, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-marker">{index + 1}</div>
                    <div className="timeline-content">
                      <h4>{month.activity}</h4>
                      <p>{month.description}</p>
                      <ul>
                        {month.tasks.map((task, i) => (
                          <li key={i}>{task}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              <div className="calendar-note">
                <FaInfoCircle style={{ marginRight: '8px' }} />
                <strong>Note:</strong> This is a general timeline. Actual timing may vary based on your location, 
                climate, and specific crop variety. Consult local agricultural experts for precise recommendations.
              </div>
            </div>
          )}

          {/* Saved Guides Tab */}
          {activeTab === 'saved' && (
            <div>
              <div className="section-header">
                <FaBookOpen className="section-icon" />
                <h2>Saved Guides</h2>
              </div>
              {savedGuides.length > 0 ? (
                <div className="saved-guides-grid">
                  {savedGuides.map(sg => (
                    <div
                      key={sg.id}
                      className="saved-guide-card"
                      onClick={() => {
                        setCrop(sg.crop);
                        setScale(sg.scale);
                        setLocation(sg.location);
                        setSoilType(sg.soilType);
                        setGuide(sg.guide);
                        setActiveTab('guide');
                      }}
                    >
                      <div className="saved-guide-header">
                        <div className="saved-guide-crop">{sg.crop}</div>
                        <div className="saved-guide-date">{sg.date}</div>
                        <button 
                          className="delete-guide-btn"
                          onClick={(e) => deleteGuide(sg.id, e)}
                          title="Delete this guide"
                        >
                          <FaTrash />
                        </button>
                      </div>
                      <div className="saved-guide-details">
                        <div><strong>Scale:</strong> {sg.scale}</div>
                        {sg.location && <div><strong>Location:</strong> {sg.location}</div>}
                        {sg.soilType && <div><strong>Soil:</strong> {sg.soilType}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FaBookOpen className="empty-state-icon" />
                  <p className="empty-state-text">No saved guides yet. Save your first guide!</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArtificialHarvesting;
